import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input, Textarea } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Badge } from "../ui/badge";
import {
  Plus,
  Edit,
  Trash2,
  Shield,
  Users,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  X,
} from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/authContext";
import { toast } from "react-hot-toast";

interface Role {
  id: number;
  name: string;
  description?: string;
  can_download_excel?: boolean;
  can_upload_excel?: boolean;
  created_at: string;
}

interface PermissionGroup {
  id: number;
  name: string;
  route?: string;
  created_at: string;
}

interface Permission {
  id: number;
  permission: string;
  description?: string;
  permission_groups_ref: number;
  created_at: string;
}

const RolesAndPermissions: React.FC = () => {
  useAuth();
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>(
    [],
  );
  // Cache of which permission group IDs are assigned to each role (role_id -> group_id[])
  const [roleGroupIds, setRoleGroupIds] = useState<Record<number, number[]>>(
    {},
  );
  const [, setPermissions] = useState<Permission[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<PermissionGroup | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [groupPermissions, setGroupPermissions] = useState<
    Record<number, Permission[]>
  >({});

  // Role states
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleForm, setRoleForm] = useState({
    name: "",
    description: "",
    can_download_excel: false,
    can_upload_excel: false,
  });
  // Permission Group states
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<PermissionGroup | null>(
    null,
  );
  const [groupForm, setGroupForm] = useState<{
    name: string;
    route: string;
    existingGroupId?: number;
  }>({
    name: "",
    route: "",
  });
  const [isCustomGroup, setIsCustomGroup] = useState(false);

  // Permission states
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [editingPermission, setEditingPermission] = useState<Permission | null>(
    null,
  );
  const [permissionForm, setPermissionForm] = useState({
    permission: "",
    description: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  // When the selected role changes, fetch which permission groups
  // are actually linked to that role via role_permissions.
  useEffect(() => {
    const loadRoleGroups = async () => {
      if (!selectedRole) return;

      // Avoid refetching if we already have data for this role
      if (roleGroupIds[selectedRole.id]) return;

      try {
        const res = await api.getPermissionGroupsByRole(selectedRole.id);
        console.log("Permission groups by role response:", res);
        let data: any[] = [];
        if (Array.isArray(res)) data = res;
        else if (Array.isArray(res?.data)) data = res.data;
        else if (Array.isArray(res?.results)) data = res.results;

        const ids = data.map((g: any) => g.id).filter((id: any) => id != null);
        setRoleGroupIds(prev => ({
          ...prev,
          [selectedRole.id]: ids,
        }));
      } catch (e) {
        console.error("Failed to fetch permission groups for role", e);
      }
    };

    loadRoleGroups();
  }, [selectedRole, roleGroupIds]);

  // For now, show all permission groups for all roles.
  // Which permissions are actually active for a role is determined
  // purely by the role_permissions table via getPermissionsByGroup.
  // Visible groups:
  // - Show only groups that have at least one permission linked to the
  //   currently selected role (via role_permissions).
  // - If no role is selected, show all groups.
  const visibleGroups: PermissionGroup[] = React.useMemo(() => {
    if (!selectedRole) {
      return permissionGroups;
    }
    const ids = roleGroupIds[selectedRole.id];
    if (!ids || ids.length === 0) {
      return [];
    }
    return permissionGroups.filter(g => ids.includes(g.id));
  }, [selectedRole, permissionGroups, roleGroupIds]);

  // Pre-fetch permissions for all visible groups so the count badge is accurate
  // biome-ignore lint/correctness/useExhaustiveDependencies: groupPermissions excluded intentionally — including it causes infinite refetch
  useEffect(() => {
    if (visibleGroups.length === 0) return;
    visibleGroups.forEach(group => {
      if (!groupPermissions[group.id]) {
        fetchPermissions(group.id);
      }
    });
  }, [visibleGroups]);

  // Groups available to add to the selected role:
  // all groups from the DB MINUS ones already assigned to this role.
  // This ensures every row in permission_groups is reachable, regardless of
  // whether it has been linked to Super Admin or any other role.
  const availableGroups = React.useMemo(() => {
    if (!selectedRole) return permissionGroups;
    const assignedIds = new Set(roleGroupIds[selectedRole.id] || []);
    return permissionGroups.filter(g => !assignedIds.has(g.id));
  }, [selectedRole, permissionGroups, roleGroupIds]);

  const fetchData = async () => {
    setLoading(true);
    try {
      console.log("Fetching roles and permission groups...");
      const [rolesRes, groupsRes] = await Promise.all([
        api.getRoles(),
        api.getPermissionGroups(),
      ]);

      console.log("Roles response:", rolesRes);
      console.log("Groups response:", groupsRes);

      // Be tolerant of different backend shapes:
      // - { success, data: [...] }
      // - { results: [...] }
      // - plain array [...]
      let rolesData: any[] = [];
      if (Array.isArray(rolesRes)) {
        rolesData = rolesRes;
      } else if (Array.isArray(rolesRes?.data)) {
        rolesData = rolesRes.data;
      } else if (Array.isArray(rolesRes?.results)) {
        rolesData = rolesRes.results;
      }

      if (rolesData.length > 0) {
        setRoles(rolesData as Role[]);
        console.log("Set roles:", rolesData);

        // Auto-select the first role if no role is currently selected
        if (!selectedRole) {
          setSelectedRole(rolesData[0] as Role);
        }
      } else if (rolesRes?.success === false) {
        console.error("Failed to fetch roles:", rolesRes?.error);
      }

      let groupsData: any[] = [];
      if (Array.isArray(groupsRes)) {
        groupsData = groupsRes;
      } else if (Array.isArray(groupsRes?.data)) {
        groupsData = groupsRes.data;
      } else if (Array.isArray(groupsRes?.results)) {
        groupsData = groupsRes.results;
      }

      if (groupsData.length > 0) {
        setPermissionGroups(groupsData as PermissionGroup[]);
        console.log("Set permission groups:", groupsData);
      } else if (groupsRes?.success === false) {
        console.error("Failed to fetch permission groups:", groupsRes?.error);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async (group_id: number) => {
    try {
      console.log("Fetching permissions for group:", group_id);
      const response = await api.getPermissionsByGroup(
        group_id,
        selectedRole?.id,
      );
      console.log("Permissions response:", response);

      if (response?.success) {
        const permissionsData = response.data || [];
        setGroupPermissions(prev => ({
          ...prev,
          [group_id]: permissionsData,
        }));
        console.log("Set permissions for group:", group_id, permissionsData);
      } else {
        console.error("Failed to fetch permissions:", response?.error);
        setGroupPermissions(prev => ({
          ...prev,
          [group_id]: [],
        }));
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setGroupPermissions(prev => ({
        ...prev,
        [group_id]: [],
      }));
    }
  };

  const toggleGroupExpansion = (group: PermissionGroup) => {
    const newExpandedGroups = new Set(expandedGroups);
    if (expandedGroups.has(group.id)) {
      newExpandedGroups.delete(group.id);
    } else {
      newExpandedGroups.add(group.id);
      // Fetch permissions if not already loaded
      if (!groupPermissions[group.id]) {
        fetchPermissions(group.id);
      }
    }
    setExpandedGroups(newExpandedGroups);
  };

  const handleRoleClick = async (role: Role) => {
    setSelectedRole(role);
    // Reset right side selections when role changes
    setSelectedGroup(null);
    setPermissions([]);
    setGroupPermissions({});
    setExpandedGroups(new Set());
  };

  // Role handlers
  const handleCreateRole = async () => {
    try {
      const response = await api.createRole({
        ...roleForm,
      });

      if (response?.success) {
        // Refresh the roles list and auto-select the newly created role
        await fetchData();
        if (response.data) {
          setSelectedRole(response.data);
        }
        setRoleDialogOpen(false);
        resetRoleForm();
        toast.success("Role created successfully!");
      } else {
        toast.error(response?.error || "Failed to create role");
      }
    } catch (error) {
      console.error("Error creating role:", error);
      toast.error("Failed to create role");
    }
  };

  const handleUpdateRole = async () => {
    if (!editingRole) return;

    try {
      const response = await api.updateRole(editingRole.id, {
        ...roleForm,
      });

      if (response?.success) {
        // Refresh the roles list
        await fetchData();
        setRoleDialogOpen(false);
        setEditingRole(null);
        resetRoleForm();
        toast.success("Role updated successfully!");
      } else {
        toast.error(response?.error || "Failed to update role");
      }
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error("Failed to update role");
    }
  };

  const handleDeleteRole = async (roleId: number) => {
    try {
      const response = await api.deleteRole(roleId);
      if (response?.success) {
        // Refresh the roles list
        await fetchData();
        toast.success("Role deleted successfully!");
      } else {
        toast.error(response?.error || "Failed to delete role");
      }
    } catch (error) {
      console.error("Error deleting role:", error);
      toast.error("Failed to delete role");
    }
  };

  const resetRoleForm = () => {
    setRoleForm({
      name: "",
      description: "",
      can_download_excel: false,
      can_upload_excel: false,
    });
    setEditingRole(null);
  };

  const openRoleDialog = (role?: Role) => {
    if (role) {
      setEditingRole(role);
      setRoleForm({
        name: role.name,
        description: role.description || "",
        can_download_excel: role.can_download_excel ?? false,
        can_upload_excel: role.can_upload_excel ?? false,
      });
    } else {
      resetRoleForm();
    }
    setRoleDialogOpen(true);
  };

  // Permission Group handlers
  const handleCreateGroup = async () => {
    if (!groupForm.name.trim()) {
      toast.error("Group name is required");
      return;
    }

    try {
      // If an existing group was selected from the dropdown, link its permissions instead of creating new
      if (groupForm.existingGroupId && selectedRole) {
        const response = await api.linkGroupToRole(
          selectedRole.id,
          groupForm.existingGroupId,
        );
        if (response?.success) {
          // Invalidate cache for this role
          setRoleGroupIds(prev => {
            const next = { ...prev };
            delete next[selectedRole.id];
            return next;
          });
          setGroupDialogOpen(false);
          resetGroupForm();
          toast.success("Group added to role successfully!");
          return;
        }
        toast.error(response?.error || "Failed to link group to role");
        return;
      }

      const response = await api.createPermissionGroup({
        name: groupForm.name,
        route: groupForm.route,
        role_id: selectedRole?.id,
      });

      if (response?.success) {
        // Refresh the permission groups list
        await fetchData();

        // Force refresh of the role-group mappings for the current role
        if (selectedRole) {
          console.log(
            `[CreateGroup] Invalidating cache for role ${selectedRole.id}`,
          );
          setRoleGroupIds(prev => {
            const next = { ...prev };
            delete next[selectedRole.id];
            return next;
          });
        }

        setGroupDialogOpen(false);
        resetGroupForm();
        toast.success("Permission group created successfully!");
      } else {
        toast.error(response?.error || "Failed to create permission group");
      }
    } catch (error) {
      console.error("Error creating permission group:", error);
      toast.error("Failed to create permission group");
    }
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup) return;

    try {
      const response = await api.updatePermissionGroup(editingGroup.id, {
        ...groupForm,
      });

      if (response?.success) {
        // Refresh the permission groups list
        await fetchData();
        setGroupDialogOpen(false);
        setEditingGroup(null);
        resetGroupForm();
        toast.success("Permission group updated successfully!");
      } else {
        toast.error(response?.error || "Failed to update permission group");
      }
    } catch (error) {
      console.error("Error updating permission group:", error);
      toast.error("Failed to update permission group");
    }
  };

  const resetGroupForm = () => {
    setGroupForm({ name: "", route: "", existingGroupId: undefined });
    setEditingGroup(null);
    setIsCustomGroup(false);
  };

  const openGroupDialog = (group?: PermissionGroup) => {
    if (group) {
      setEditingGroup(group);
      setGroupForm({
        name: group.name,
        route: group.route || "",
      });
      setIsCustomGroup(true); // Always show inputs when editing
    } else {
      resetGroupForm();
      setIsCustomGroup(false);
    }
    setGroupDialogOpen(true);
  };

  // Permission handlers
  const handleCreatePermission = async () => {
    // Find the group that was last expanded or use selectedGroup
    const targetGroup =
      selectedGroup ||
      (expandedGroups.size > 0
        ? permissionGroups.find(g => expandedGroups.has(g.id))
        : null);

    if (!targetGroup) {
      toast.error("Please select a permission group first");
      return;
    }

    if (!selectedRole) {
      toast.error("Please select a role first");
      return;
    }

    try {
      const response = await api.createPermission({
        ...permissionForm,
        permission_groups_ref: targetGroup.id,
        role_id: selectedRole.id,
      });

      if (response?.success) {
        // Refresh the permissions list for the target group
        await fetchPermissions(targetGroup.id);
        setPermissionDialogOpen(false);
        resetPermissionForm();
        toast.success("Permission created successfully!");
      } else {
        toast.error(response?.error || "Failed to create permission");
      }
    } catch (error) {
      console.error("Error creating permission:", error);
      toast.error("Failed to create permission");
    }
  };

  const handleUpdatePermission = async () => {
    if (!editingPermission) return;

    try {
      const response = await api.updatePermission(editingPermission.id, {
        ...permissionForm,
      });

      if (response?.success) {
        // Refresh the permissions list for the group that contains this permission
        await fetchPermissions(editingPermission.permission_groups_ref);
        setPermissionDialogOpen(false);
        setEditingPermission(null);
        resetPermissionForm();
        toast.success("Permission updated successfully!");
      } else {
        toast.error(response?.error || "Failed to update permission");
      }
    } catch (error) {
      console.error("Error updating permission:", error);
      toast.error("Failed to update permission");
    }
  };

  const handleUnlinkPermission = async (permissionId: number) => {
    if (!selectedRole) {
      toast.error("Select a role first");
      return;
    }

    try {
      // Find the permission to locate its group for refresh
      const permission = Object.values(groupPermissions)
        .flat()
        .find(p => p.id === permissionId);

      const response = await api.unlinkPermissionFromRole(
        selectedRole.id,
        permissionId,
      );

      if (response?.success) {
        if (permission) {
          await fetchPermissions(permission.permission_groups_ref);
        }
        toast.success("Permission removed from role");
      } else {
        toast.error(response?.error || "Failed to remove permission from role");
      }
    } catch (error) {
      console.error("Error unlinking permission from role:", error);
      toast.error("Failed to remove permission from role");
    }
  };

  const handleUnlinkGroupFromRole = async (groupId: number) => {
    if (!selectedRole) {
      toast.error("Select a role first");
      return;
    }

    try {
      const perms = groupPermissions[groupId] || [];
      if (perms.length === 0) {
        // If not loaded yet, we can't unlink. But in the UI it's usually loaded before they can click it.
        toast.error("Group is empty or not loaded");
        return;
      }

      // Unlink all permissions in parallel
      await Promise.all(
        perms.map(p => api.unlinkPermissionFromRole(selectedRole.id, p.id)),
      );

      // Refresh permissions for this group; since there are 0 left, it will update empty
      await fetchPermissions(groupId);

      // Invalidate the cache of groups for this role so it disappears from the left sidebar
      setRoleGroupIds(prev => {
        const next = { ...prev };
        delete next[selectedRole.id];
        return next;
      });
      // Refetch to cleanly update the UI
      fetchData();

      toast.success("Group removed from role successfully");
    } catch (error) {
      console.error("Error unlinking group from role:", error);
      toast.error("Failed to remove group from role");
    }
  };

  const resetPermissionForm = () => {
    setPermissionForm({ permission: "", description: "" });
    setEditingPermission(null);
  };

  const openPermissionDialog = (permission?: Permission) => {
    if (permission) {
      // Edit mode — pre-fill with existing values
      setEditingPermission(permission);
      setPermissionForm({
        permission: permission.permission,
        description: permission.description || "",
      });
    } else {
      // Create mode — auto-derive slug from the target group's route
      const targetGroup =
        selectedGroup || permissionGroups.find(g => expandedGroups.has(g.id));

      const derivedSlug = targetGroup?.route
        ? `view_${targetGroup.route.replace(/^\//, "").replace(/\//g, "_")}`
        : "";

      setPermissionForm({ permission: derivedSlug, description: "" });
      setEditingPermission(null);
    }
    setPermissionDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Roles and Permissions
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage user roles and their associated permissions
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Side - Roles (30%) */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Roles
              </CardTitle>
              <Button onClick={() => openRoleDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Add Role
              </Button>
            </CardHeader>
            <CardContent>
              {roles.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No roles found</p>
                  <p className="text-sm">
                    Create your first role to get started
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {roles.map(role => (
                    <div
                      key={role.id}
                      className={`flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer ${
                        selectedRole?.id === role.id
                          ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
                          : ""
                      }`}
                      onClick={() => handleRoleClick(role)}
                      onKeyDown={e => e.key === "Enter" && handleRoleClick(role)}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{role.name}</h3>
                        </div>
                        {role.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {role.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openRoleDialog(role)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Role</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete the role "
                                {role.name}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteRole(role.id)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Side - Permission Groups with Accordion (70%) */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Permission Groups
              </CardTitle>
              <Button onClick={() => openGroupDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Add Group
              </Button>
            </CardHeader>
            <CardContent>
              {visibleGroups.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No permission groups found</p>
                  <p className="text-sm">
                    Create your first permission group to get started
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {visibleGroups.map(group => {
                    const isExpanded = expandedGroups.has(group.id);
                    const groupPerms = groupPermissions[group.id] || [];

                    return (
                      <div key={group.id}>
                        {/* Group Header */}
                        <div
                          className="p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center justify-between"
                          onClick={() => toggleGroupExpansion(group)}
                          onKeyDown={e => e.key === "Enter" && toggleGroupExpansion(group)}
                        >
                          <div className="flex items-center gap-2">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                            <h3 className="font-medium">{group.name}</h3>
                            {group.route && (
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                ({group.route})
                              </span>
                            )}
                            <Badge
                              variant="secondary"
                              className="text-xs px-1.5 py-0"
                            >
                              {groupPerms.length}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={e => {
                                e.stopPropagation();
                                setSelectedGroup(group);
                                openPermissionDialog();
                              }}
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Add Permission
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={e => {
                                e.stopPropagation();
                                openGroupDialog(group);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={e => e.stopPropagation()}
                                  title="Remove Group From Role"
                                >
                                  <Trash2 className="h-4 w-4 text-orange-500" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    Remove Group From Role
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to remove all
                                    permissions in the "{group.name}" group from
                                    the <strong>{selectedRole?.name}</strong>{" "}
                                    role? The group and its permissions will
                                    remain available globally for other roles.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() =>
                                      handleUnlinkGroupFromRole(group.id)
                                    }
                                  >
                                    Remove
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>

                        {/* Expanded Permissions */}
                        {isExpanded &&
                          (() => {
                            const filteredGroupPerms = groupPerms;

                            return (
                              <div className="pl-6 border-l border-gray-300 dark:border-gray-600 ml-2">
                                <div className="p-3">
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {filteredGroupPerms.map(permission => (
                                      <span
                                        key={permission.id}
                                        className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs font-medium group"
                                        title={
                                          permission.description ||
                                          permission.permission
                                        }
                                      >
                                        <span>{permission.permission}</span>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleUnlinkPermission(
                                              permission.id,
                                            )
                                          }
                                          className="ml-1 rounded-full hover:bg-gray-300 dark:hover:bg-gray-500 p-0.5 opacity-60 hover:opacity-100"
                                          title="Remove from role"
                                        >
                                          <X className="h-2.5 w-2.5" />
                                        </button>
                                      </span>
                                    ))}
                                    {filteredGroupPerms.length === 0 && (
                                      <p className="text-gray-500 text-sm">
                                        No permissions in this group for this
                                        role.
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Role Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingRole ? "Edit Role" : "Create Role"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="role-name">Name</Label>
              <Input
                id="role-name"
                value={roleForm.name}
                onChange={e =>
                  setRoleForm({ ...roleForm, name: e.target.value })
                }
                placeholder="Enter role name"
              />
            </div>
            <div>
              <Label htmlFor="role-description">Description</Label>
              <Textarea
                id="role-description"
                value={roleForm.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setRoleForm({ ...roleForm, description: e.target.value })
                }
                placeholder="Enter role description"
              />
            </div>

            {/* Excel Permissions */}
            <div className="space-y-3 pt-4 border-t">
              <h4 className="text-sm font-medium">Excel Permissions</h4>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="can-download-excel">
                    Allow Excel Download
                  </Label>
                  <p className="text-sm text-gray-500">
                    Allow users with this role to download Excel files
                  </p>
                </div>
                <Switch
                  id="can-download-excel"
                  checked={roleForm.can_download_excel}
                  onCheckedChange={checked =>
                    setRoleForm({ ...roleForm, can_download_excel: checked })
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="can-upload-excel">Allow Excel Upload</Label>
                  <p className="text-sm text-gray-500">
                    Allow users with this role to upload Excel files
                  </p>
                </div>
                <Switch
                  id="can-upload-excel"
                  checked={roleForm.can_upload_excel}
                  onCheckedChange={checked =>
                    setRoleForm({ ...roleForm, can_upload_excel: checked })
                  }
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setRoleDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={editingRole ? handleUpdateRole : handleCreateRole}
              >
                {editingRole ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Permission Group Dialog */}
      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingGroup
                ? "Edit Permission Group"
                : "Create Permission Group"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!editingGroup && !isCustomGroup && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="group-select">Select Permission Group</Label>
                  <Select
                    value={
                      groupForm.existingGroupId !== undefined
                        ? groupForm.existingGroupId.toString()
                        : ""
                    }
                    onValueChange={val => {
                      if (val === "custom") {
                        setIsCustomGroup(true);
                        setGroupForm({
                          name: "",
                          route: "",
                          existingGroupId: undefined,
                        });
                      } else {
                        const group = permissionGroups.find(
                          g => String(g.id) === val,
                        );
                        if (group) {
                          setGroupForm({
                            name: group.name,
                            route: group.route || "",
                            existingGroupId: group.id,
                          });
                        }
                      }
                    }}
                  >
                    <SelectTrigger id="group-select">
                      <SelectValue placeholder="Select a group..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem
                        value="custom"
                        className="font-semibold text-blue-600"
                      >
                        + Add Custom Group...
                      </SelectItem>
                      {availableGroups.map(g => (
                        <SelectItem
                          key={`group-${g.id}`}
                          value={g.id.toString()}
                        >
                          {g.name} {g.route ? `(${g.route})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {groupForm.name && groupForm.existingGroupId && (
                  <div>
                    <Label>App Route</Label>
                    <Input
                      value={groupForm.route || "No Route Assigned"}
                      disabled
                    />
                  </div>
                )}
              </div>
            )}

            {(editingGroup || isCustomGroup) && (
              <>
                <div>
                  <Label htmlFor="group-name">Name</Label>
                  <Input
                    id="group-name"
                    value={groupForm.name}
                    onChange={e =>
                      setGroupForm({ ...groupForm, name: e.target.value })
                    }
                    placeholder="Enter group name"
                  />
                </div>
                <div>
                  <Label htmlFor="group-route">App Route</Label>
                  <Input
                    id="group-route"
                    value={groupForm.route}
                    onChange={e =>
                      setGroupForm({ ...groupForm, route: e.target.value })
                    }
                    placeholder="e.g. /b2c_products_mapping"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    The frontend route this group controls access to (e.g.
                    /orders, /b2c_products_mapping)
                  </p>
                </div>
                {!editingGroup && (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setIsCustomGroup(false)}
                    className="px-0 h-auto"
                  >
                    &larr; Back to select existing group
                  </Button>
                )}
              </>
            )}

            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setGroupDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={editingGroup ? handleUpdateGroup : handleCreateGroup}
              >
                {editingGroup ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Permission Dialog */}
      <Dialog
        open={permissionDialogOpen}
        onOpenChange={setPermissionDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingPermission ? "Edit Permission" : "Create Permission"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!editingPermission && (
              <div>
                <Label>Permission Group</Label>
                <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded border">
                  {selectedGroup
                    ? selectedGroup.name
                    : expandedGroups.size > 0
                      ? permissionGroups.find(g => expandedGroups.has(g.id))
                          ?.name || "No group selected"
                      : "No group selected"}
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="permission-name">Permission Slug</Label>
              <Input
                id="permission-name"
                value={permissionForm.permission}
                onChange={e =>
                  setPermissionForm({
                    ...permissionForm,
                    permission: e.target.value,
                  })
                }
                placeholder="e.g. view_b2c_products_mapping"
              />
              {!editingPermission &&
                (() => {
                  const targetGroup =
                    selectedGroup ||
                    permissionGroups.find(g => expandedGroups.has(g.id));
                  return targetGroup?.route ? (
                    <p className="text-xs text-gray-500 mt-1">
                      Auto-filled from group route ({targetGroup.route}). You
                      can edit if needed.
                    </p>
                  ) : (
                    <p className="text-xs text-orange-500 mt-1">
                      No route set on this group. Set a route on the group
                      first, or enter the slug manually.
                    </p>
                  );
                })()}
            </div>
            <div>
              <Label htmlFor="permission-description">Description</Label>
              <Textarea
                id="permission-description"
                value={permissionForm.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setPermissionForm({
                    ...permissionForm,
                    description: e.target.value,
                  })
                }
                placeholder="Enter permission description"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setPermissionDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={
                  editingPermission
                    ? handleUpdatePermission
                    : handleCreatePermission
                }
              >
                {editingPermission ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RolesAndPermissions;
